import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import '../../scss/components/InitialCheckForm.scss';
import Form from './Form.tsx';
import Button from './Button.tsx';
import SingleCheckbox from './SingleCheckbox.tsx';
import Radios from './Radios.tsx';
import FileDropzone, { UploadedFile } from './FileDropzone.tsx';
import ExamTypeAccountSelect from '../ClientAccounts/ExamTypeAccountSelect.tsx';
import DynamicContent from '../DynamicContent/DynamicContent.tsx';
import { IClientAccount } from '../../interface/IClientAccount.ts';
import { ICertificate } from '../../interface/ICertificate.ts';
import { IOption } from '../../interface/IOption.ts';
import { iso3166 } from '../../static/iso3166.js';
import { useProfileContext } from '../../features/auth/hooks/use-profile-context.ts';
import { useCertificatesApi } from '../../features/auth/hooks/use-certifictates-api.ts';
import { useVerificationApi } from '../../features/auth/hooks/use-verification-api.ts';
import { useClientAccountApi } from '../../features/auth/hooks/use-client-account-api.ts';
import { useListsApi } from '../../features/auth/hooks/use-lists-api.ts';
import { useOrdersApi } from '../../features/auth/hooks/use-orders-api.ts';
import { usePricesApi } from '../../features/auth/hooks/use-prices-api.ts';
import { useDocumentApi } from '../../features/auth/hooks/use-document-api.ts';
import { useHelper } from '../../features/functions/helper.tsx';
import { useValidators } from '../../features/functions/validators.tsx';
import { readDraft, writeDraft, clearDraft } from '../../features/functions/verificationDraft.ts';
import { getExamRegistrationStatus, IExamInfo } from '../../features/functions/examRegistration.ts';
import { ApiResponseType } from '../../features/api/types.ts';

// short: billing headings, long: review summary, label: the checkbox itself.
const EXAM_TYPES = [
    { value: 'exam_type_efa_cfp', short: 'EFA / CFP', long: 'EFPA European Financial Advisor EFA® / CERTIFIED FINANCIAL PLANNER®', label: 'Zentralprüfung für Bewerber zur Zertifizierung als EFPA European Financial Advisor EFA® / CERTIFIED FINANCIAL PLANNER® - Zertifikatsträger' },
    { value: 'exam_type_din_competence', short: 'DIN-Kompetenzprüfung', long: 'DIN-Kompetenzprüfung', label: 'Zeitgleich DIN-Kompetenzprüfung für Bewerber zur Zertifizierung zum geprüften privaten Finanzplaner nach DIN ISO 22222:2006' },
    { value: 'exam_type_cga_cfep', short: 'CGA / CFEP', long: 'CGA® CERTIFIED GENERATIONS ADVISOR / CFEP® CERTIFIED FOUNDATION AND ESTATE PLANNER', label: 'Zentralprüfung für Bewerber zur Zertifizierung als CGA® CERTIFIED GENERATIONS ADVISOR / CFEP® CERTIFIED FOUNDATION AND ESTATE PLANNER - Zertifikatsträger' },
];

const PARTICIPATION_OPTIONS = [
    { value: 'participation_first_attempt', label: 'erstmalige Teilnahme' },
    { value: 'participation_repeat', label: '1. Wiederholung' },
    { value: 'participation_second_repeat', label: '2. Wiederholung' },
];


// Which Zentralprüfung an existing certificate holder may still book: EFA/CFP
// (or DIN) holders only the CGA/CFEP exam, CGA/CFEP holders only the EFA/CFP
// exam. Certificate types compared without spaces, case-insensitive (as in
// TabCertificates); honorary certificates count like their regular type.
const EFA_GROUP_CERTIFICATES = ['efa', 'cfp', 'honcfp', 'din-iso22222'];
const CGA_GROUP_CERTIFICATES = ['cga', 'cfep', 'honcfep'];

/** null: no restriction (no relevant certificate); []: nothing left to book. */
const examTypesForCertificates = (certificates:Array<ICertificate>):string[]|null => {
    const types = certificates.map(certificate => String(certificate.type ?? '').replace(/\s+/g, '').toLowerCase());
    const hasEfaGroup = types.some(type => EFA_GROUP_CERTIFICATES.includes(type));
    const hasCgaGroup = types.some(type => CGA_GROUP_CERTIFICATES.includes(type));
    if (!hasEfaGroup && !hasCgaGroup) {
        return null;
    }
    const allowed:string[] = [];
    if (hasEfaGroup && !hasCgaGroup) {
        allowed.push('exam_type_cga_cfep');
    }
    if (hasCgaGroup && !hasEfaGroup) {
        allowed.push('exam_type_efa_cfp');
    }
    return allowed;
};

const DIN_WITHOUT_EFA_WARNING = 'Die DIN-Kompetenzprüfung sollte zusammen mit der EFA® / CERTIFIED FINANCIAL PLANNER® - Zentralprüfung gebucht werden!';

const CERTIFICATE_PROOF_HINT = 'Ohne Nachweis des Abschlusses von Level I eines akkreditierten Aus- und Weiterbildungsprogramms erfolgt keine Zulassung zur Zentralprüfung. Eine Teilnahme ist ausgeschlossen.';

const CERTIFICATE_PROOF_OPTIONS = [
    { value: 'certificate_attached', label: 'lade ich mit der Anmeldung hoch' },
    { value: 'certificate_to_be_submitted', label: 'reiche ich bis spätestens zum Anmeldeschluss nach' },
];

type InlineAccountState = {
    draft:IClientAccount,
    submitAttempted:boolean,
    saving:boolean,
    saveError:string,
};

const INITIAL_FORM_DATA:any = {
    exam_type_efa_cfp: false,
    exam_type_din_competence: false,
    exam_type_cga_cfep: false,
    participation: '',
    // Entry id from the "Aus- und Weiterbildungsprogramme" list.
    education_program: '',
    certificate_proof: '',
    accept_exam_regulations: false,
    accept_agb: false,
    accept_privacy: false,
    accept_withdrawal: false,
    request_early_check: false,
};

const DEFAULT_SUCCESS_MESSAGE = 'Ihre Anmeldung wurde übermittelt und wird vom FPSB Deutschland geprüft. Sie erhalten eine Rückmeldung per E-Mail.';

const GENERIC_SUBMIT_ERROR = 'Das Formular konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.';

const extractSubmitError = (payload:any):string => {
    if (!payload || typeof payload !== 'object') {
        return GENERIC_SUBMIT_ERROR;
    }

    if (payload.errors && typeof payload.errors === 'object') {
        const messages = Object.values(payload.errors)
            .filter((message):message is string => typeof message === 'string' && message.trim() !== '');
        if (messages.length > 0) {
            return messages.join(' ');
        }
    }

    if (typeof payload.message === 'string' && payload.message.trim() !== '') {
        return payload.message;
    }

    if (typeof payload.error === 'string' && payload.error.trim() !== '') {
        return payload.error;
    }

    return GENERIC_SUBMIT_ERROR;
};

interface VerificationFormProps {
    heading:string,
    intro?:ReactNode,
    introContentTitle?:string,
    successMessage?:string,
    draftKey?:string,
}

export default function VerificationForm({
    heading,
    intro,
    introContentTitle,
    successMessage = DEFAULT_SUCCESS_MESSAGE,
    draftKey = 'default',
}:VerificationFormProps) {
    const { postVerification } = useVerificationApi();
    const { getClientAccounts, postClientAccount } = useClientAccountApi();
    const { getList } = useListsApi();
    const { getPrices, getNextExam } = usePricesApi();
    const { postOrderEarlyCheck } = useOrdersApi();
    const { getVerificationDocument } = useDocumentApi();
    const { formatDate, formatDateFromTimestamp } = useHelper();
    const { validateEmail } = useValidators();
    const { profile, isRoleValidated } = useProfileContext();
    const { getCertificates } = useCertificatesApi();
    const location = useLocation();

    // A failed attempt can navigate here with prefilled answers (see orderToFormData).
    const retryPrefill = (location.state as any)?.prefill ?? null;
    const storedDraft = useRef(readDraft(draftKey)).current;
    const draftData = storedDraft?.data ?? null;

    const [formData, setFormData] = useState<any>(() => {
        const initial = {
            ...INITIAL_FORM_DATA,
            ...(draftData?.formData ?? {}),
            ...(retryPrefill ?? {}),
        };
        // A draft or retried order may still hold a proof option that no longer exists.
        if (!CERTIFICATE_PROOF_OPTIONS.some(option => option.value === initial.certificate_proof)) {
            initial.certificate_proof = '';
        }
        return initial;
    });
    // "Aus- und Weiterbildungsprogramme" entries; the "kein Abschluss" one carries no_degree.
    const [educationPrograms, setEducationPrograms] = useState<Array<any>>([]);
    const [files, setFiles] = useState<Array<UploadedFile>>([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [responseMessageClass, setResponseMessageClass] = useState('');
    const [touched, setTouched] = useState(false);
    // Confirmation step shown after a successful submit (replaces the redirect).
    const [submitted, setSubmitted] = useState(false);
    const [createdOrderIds, setCreatedOrderIds] = useState<Array<number|string>>([]);
    const [earlyCheckRequested, setEarlyCheckRequested] = useState(false);
    const [earlyCheckLoading, setEarlyCheckLoading] = useState(false);
    const [earlyCheckError, setEarlyCheckError] = useState('');
    // Exam fees per exam-type field, resolved server-side: { exam_type_efa_cfp: { net, gross }, ... }
    const [prices, setPrices] = useState<any>({});
    // DIN-Kompetenzprüfung: only pick a separate billing account when explicitly requested.
    const [dinOtherBilling, setDinOtherBilling] = useState(draftData?.dinOtherBilling ?? false);
    // Review/checkout step: after "Weiter" the form is replaced by a read-only summary.
    const [showReview, setShowReview] = useState(false);
    // Next exam { exam_date, registration_deadline, registration_open } (unix seconds) shown in the review.
    // Also carries allowed_exam_types / linked_exam_number (one registration per exam project).
    // null after loading means there is no upcoming exam project.
    const [examInfo, setExamInfo] = useState<IExamInfo|null>(null);
    const [examInfoLoaded, setExamInfoLoaded] = useState(false);
    const [examInfoFailed, setExamInfoFailed] = useState(false);
    // Exam types the advisor's certificates still allow (see examTypesForCertificates).
    // Only validated advisors hold certificates; the backend rejects the call otherwise.
    const [certificateExamTypes, setCertificateExamTypes] = useState<string[]|null>(null);
    const [certificatesLoaded, setCertificatesLoaded] = useState(!isRoleValidated);
    // Timestamp captured when the user proceeds to the review (consent time proxy).
    const [consentAt, setConsentAt] = useState<Date|null>(null);

    // Per-exam-type billing accounts. Each checked exam type can pick an existing
    // account (a real id), or leave it empty (backend picks). New accounts entered
    // inline are created immediately (returning an id), so only ids are ever held.
    const [clientAccounts, setClientAccounts] = useState<Array<IClientAccount>>([]);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [privateType, setPrivateType] = useState<IOption>();
    const [companyType, setCompanyType] = useState<IOption>();
    const [transferTypeId, setTransferTypeId] = useState<number>();
    // assignment per exam type: an existing account id (string) or null.
    const [assignments, setAssignments] = useState<{ [examType:string]:string|null }>(() => ({
        exam_type_efa_cfp: null,
        exam_type_din_competence: null,
        exam_type_cga_cfep: null,
        ...(draftData?.assignments ?? {}),
    }));
    // A draft written while a save was running must not restore a stuck
    // "Wird gespeichert …" button or a stale error.
    const [inlineByExamType, setInlineByExamType] = useState<{ [examType:string]:InlineAccountState }>(() => {
        const restored:{ [examType:string]:InlineAccountState } = {};
        Object.entries(draftData?.inlineByExamType ?? {}).forEach(([examType, inline]:[string, any]) => {
            restored[examType] = { ...inline, saving: false, saveError: '' };
        });
        return restored;
    });
    const [dismissedInline, setDismissedInline] = useState<{ [examType:string]:boolean }>(draftData?.dismissedInline ?? {});
    // Uploads are too big for the draft storage.
    const [draftHadUpload] = useState(() => (draftData?.formData?.certificate_proof === 'certificate_attached'));

    useEffect(() => {
        getList('client_account_type').then((res:any) => {
            if (res && typeof res.forEach === 'function') {
                res.forEach((item:any) => {
                    if (item.type === 'private') setPrivateType({ value: item.id, id: item.id, label: item.label });
                    if (item.type === 'company') setCompanyType({ value: item.id, id: item.id, label: item.label });
                });
            }
        }).catch(() => {});
        getList('client_account_payment_type').then((res:any) => {
            if (res && typeof res.forEach === 'function') {
                res.forEach((item:any) => {
                    if (item.type === 'transfer') setTransferTypeId(item.id);
                });
            }
        }).catch(() => {});
        getList('education_program').then((res:any) => {
            if (Array.isArray(res)) {
                setEducationPrograms(res);
            }
        }).catch(() => {});
        loadClientAccounts().then(accounts => {
            setClientAccounts(accounts);
            // A restored draft may point at a deleted account.
            setAssignments(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(examType => {
                    if (next[examType] && !accounts.some((account:IClientAccount) => String(account.id) === String(next[examType]))) {
                        next[examType] = null;
                    }
                });
                return next;
            });
            setAccountsLoading(false);
        }).catch(() => setAccountsLoading(false));
        getPrices().then((res:any) => setPrices(res && typeof res === 'object' ? res : {})).catch(() => {});
        loadExamInfo();
        if (isRoleValidated) {
            getCertificates()
                .then((res:any) => setCertificateExamTypes(res.type === ApiResponseType.OK ? examTypesForCertificates(res.payload) : null))
                .catch(() => setCertificateExamTypes(null))
                .finally(() => setCertificatesLoaded(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Exam types the contact can still book in the upcoming exam project. Without
    // the field (older backend, or no upcoming project) every type is offered.
    const allowedExamTypes:string[] = Array.isArray(examInfo?.allowed_exam_types)
        ? examInfo!.allowed_exam_types!
        : EXAM_TYPES.map(option => option.value);
    // loading | no_exam | deadline_passed | already_registered | open, shared with Orders.
    const registrationStatus = getExamRegistrationStatus(examInfo, examInfoLoaded, examInfoFailed);
    // DIN added to an existing EFA/CFP registration: it joins that registration
    // (same exam number) and costs one Gebühreneinheit, as if booked together.
    const dinJoinsRegistration = allowedExamTypes.length === 1 && allowedExamTypes[0] === 'exam_type_din_competence';
    // EFA/CFP cancelled while its DIN order stayed: EFA/CFP may be rebooked onto that registration.
    const efaRejoinsRegistration = allowedExamTypes.length === 1 && allowedExamTypes[0] === 'exam_type_efa_cfp';
    const linkedExamNumber:string = examInfo?.linked_exam_number || '';
    // What is actually offered: the project's bookable types, narrowed by the certificates.
    const bookableExamTypes = allowedExamTypes.filter(examType => certificateExamTypes === null || certificateExamTypes.includes(examType));
    const offeredExamTypes = EXAM_TYPES.filter(option => bookableExamTypes.includes(option.value));

    // A draft or retried order may hold exam types that are no longer bookable.
    useEffect(() => {
        const blocked = EXAM_TYPES
            .map(option => option.value)
            .filter(examType => formData[examType] && !bookableExamTypes.includes(examType));
        if (blocked.length === 0) {
            return;
        }
        setFormData((prev:any) => {
            const next = { ...prev };
            blocked.forEach(examType => { next[examType] = false; });
            return next;
        });
        setAssignments(prev => {
            const next = { ...prev };
            blocked.forEach(examType => { next[examType] = null; });
            return next;
        });
        blocked.forEach(examType => closeInlineForm(examType, false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookableExamTypes.join(',')]);

    function loadClientAccounts():Promise<Array<IClientAccount>> {
        // getClientAccounts guarantees an array payload on success.
        return getClientAccounts().then((data:any) => (data.type === ApiResponseType.OK ? data.payload : []));
    }

    function loadExamInfo() {
        getNextExam()
            .then((res:any) => {
                setExamInfo(res && typeof res === 'object' ? res : null);
                setExamInfoFailed(false);
            })
            .catch(() => setExamInfoFailed(true))
            .finally(() => setExamInfoLoaded(true));
    }

    // Gated on `touched`, so opening the form never stores a draft.
    useEffect(() => {
        if (!touched || submitted) {
            return;
        }
        const timer = setTimeout(() => {
            writeDraft(draftKey, {
                formData,
                dinOtherBilling,
                assignments,
                dismissedInline,
                inlineByExamType,
            }, profile?.real_number ?? null);
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [touched, submitted, formData, dinOtherBilling, assignments, dismissedInline, inlineByExamType]);

    // Latest draft state, for writes outside the debounce (a save may finish
    // after the user already left the page).
    const latestDraftRef = useRef({ formData, dinOtherBilling, assignments, dismissedInline, inlineByExamType });
    latestDraftRef.current = { formData, dinOtherBilling, assignments, dismissedInline, inlineByExamType };

    // Another contact logged in on this browser without a logout.
    useEffect(() => {
        if (!storedDraft || !profile?.real_number || !storedDraft.contactId) {
            return;
        }
        if (String(storedDraft.contactId) !== String(profile.real_number)) {
            clearDraft(draftKey);
            setFormData({ ...INITIAL_FORM_DATA, ...(retryPrefill ?? {}) });
            setDinOtherBilling(false);
            setAssignments({ exam_type_efa_cfp: null, exam_type_din_competence: null, exam_type_cga_cfep: null });
            setInlineByExamType({});
            setDismissedInline({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.real_number]);

    // Request the early eligibility check on the just-created order(s) after the fact.
    const handleRequestEarlyCheck = () => {
        setEarlyCheckLoading(true);
        setEarlyCheckError('');
        postOrderEarlyCheck(createdOrderIds).then((res:any) => {
            setEarlyCheckLoading(false);
            if (res.type === ApiResponseType.OK) {
                setEarlyCheckRequested(true);
            } else {
                setEarlyCheckError('Die sofortige Bearbeitung konnte nicht angefordert werden. Bitte versuchen Sie es später erneut.');
            }
        }).catch(() => {
            setEarlyCheckLoading(false);
            setEarlyCheckError('Der Server ist aktuell nicht erreichbar. Bitte versuchen Sie es später erneut.');
        });
    };

    const updateField = (name:string, value:any) => {
        setTouched(true);
        setFormData((prev:any) => ({ ...prev, [name]: value }));
    };

    const patchInline = (examType:string, patch:Partial<InlineAccountState>) => {
        setInlineByExamType(prev => (prev[examType]
            ? { ...prev, [examType]: { ...prev[examType], ...patch } }
            : prev));
    };

    // `dismissed` keeps the default-open effect from reopening the row.
    const closeInlineForm = (examType:string, dismissed = true) => {
        setInlineByExamType(prev => {
            if (!prev[examType]) {
                return prev;
            }
            const next = { ...prev };
            delete next[examType];
            return next;
        });
        setDismissedInline(prev => ({ ...prev, [examType]: dismissed }));
    };

    // On uncheck, drop the account assignment and the inline form.
    const toggleExamType = (examType:string) => {
        const wasChecked = formData[examType];
        setTouched(true);
        setFormData((prev:any) => {
            const next = { ...prev, [examType]: !wasChecked };
            if (isNoDegree(next.education_program) && requiresDegree(next)) {
                next.education_program = '';
            }
            return next;
        });
        if (wasChecked) {
            setAssignments(prev => ({ ...prev, [examType]: null }));
            closeInlineForm(examType, false);
        }
    };

    const handleAssign = (examType:string, value:string|null) => {
        setTouched(true);
        setAssignments(prev => ({ ...prev, [examType]: value }));
        closeInlineForm(examType);
    };

    // Toggle a separate DIN billing account. Turning it off clears any DIN assignment
    // so no separate account is submitted for the DIN exam.
    const toggleDinOtherBilling = () => {
        setTouched(true);
        setDinOtherBilling(prev => {
            const next = !prev;
            if (!next) {
                setAssignments(assignmentsPrev => ({ ...assignmentsPrev, exam_type_din_competence: null }));
                closeInlineForm('exam_type_din_competence', false);
            }
            return next;
        });
    };

    // Fetch a configured legal document and force a download (data-URI), like the SEPA form.
    // preventDefault/stopPropagation so the link click does not toggle its checkbox.
    const openLegalDocument = (e:any, key:string, baseName:string) => {
        e.preventDefault();
        e.stopPropagation();
        getVerificationDocument(key).then((res:any) => {
            if (res.type === ApiResponseType.OK && res.payload?.payload) {
                const a = document.createElement('a');
                a.href = 'data:' + res.payload.content_type + ';base64,' + res.payload.payload;
                a.download = baseName + (String(res.payload.content_type).includes('pdf') ? '.pdf' : '');
                a.click();
            }
        }).catch(() => {});
    };

    const makeEmptyDraft = ():IClientAccount => ({
        id: null,
        client_account_type: null,
        client_account_payment_type: transferTypeId ?? null,
        client_account_billing_name: '',
        client_account_department: '',
        client_account_contact_person: '',
        client_account_cost_position: '',
        client_account_budget_number: '',
        client_account_project_number: '',
        client_account_address: '',
        client_account_address_extra: '',
        client_account_zip: '',
        client_account_city: '',
        client_account_country: null,
        client_account_email: '',
        client_account_ust_id: '',
        update_allowed: true,
        assigned_certificates: [],
    });

    const makeInlineState = ():InlineAccountState => ({
        draft: makeEmptyDraft(),
        submitAttempted: false,
        saving: false,
        saveError: '',
    });

    const openAddNew = (examType:string) => {
        setTouched(true);
        setDismissedInline(prev => ({ ...prev, [examType]: false }));
        setInlineByExamType(prev => ({ ...prev, [examType]: makeInlineState() }));
    };

    // ISO codes are stored lowercase, but the CRM may hold them in any case.
    const countryOption = (code:any) => {
        const value = String(code ?? '').toLowerCase();
        return value ? (iso3166.find((entry:any) => entry.value === value) ?? null) : null;
    };

    const prefillDraft = (draft:IClientAccount, typeId:any):IClientAccount => {
        const fullName = [profile?.firstname, profile?.lastname].filter(Boolean).join(' ');
        if (typeId === companyType?.id) {
            return {
                ...draft,
                client_account_type: typeId,
                client_account_billing_name: profile?.company_company || '',
                client_account_department: profile?.company_department || '',
                client_account_contact_person: fullName,
                client_account_address: profile?.company_address || '',
                client_account_zip: profile?.company_zip || '',
                client_account_city: profile?.company_city || '',
                client_account_country: countryOption(profile?.company_country),
                client_account_email: profile?.company_email || '',
            };
        }
        return {
            ...draft,
            client_account_type: typeId,
            client_account_billing_name: fullName,
            client_account_address: profile?.private_address || '',
            client_account_zip: profile?.private_zip || '',
            client_account_city: profile?.private_city || '',
            client_account_country: countryOption(profile?.private_country),
            client_account_email: profile?.private_email || '',
            // Hidden for private accounts, but would still be submitted.
            client_account_department: '',
            client_account_contact_person: '',
            client_account_cost_position: '',
            client_account_budget_number: '',
            client_account_project_number: '',
            client_account_address_extra: '',
            client_account_ust_id: '',
        };
    };

    const handleInlineChange = (examType:string, next:IClientAccount) => {
        setTouched(true);
        setInlineByExamType(prev => {
            const current = prev[examType];
            if (!current) {
                return prev;
            }
            const typeChanged = next.client_account_type !== current.draft.client_account_type;
            return {
                ...prev,
                [examType]: {
                    ...current,
                    draft: typeChanged ? prefillDraft(next, next.client_account_type) : next,
                },
            };
        });
    };

    const validateDraft = (draft:IClientAccount) => {
        const draftErrors:any = {};
        if (!draft.client_account_type) draftErrors.client_account_type = 'Bitte wählen Sie die Art des Rechnungskontos.';
        if (!draft.client_account_billing_name) draftErrors.client_account_billing_name = 'Bitte ausfüllen.';
        if (!draft.client_account_address) draftErrors.client_account_address = 'Bitte ausfüllen.';
        if (!draft.client_account_zip) draftErrors.client_account_zip = 'Bitte ausfüllen.';
        if (!draft.client_account_city) draftErrors.client_account_city = 'Bitte ausfüllen.';
        if (!draft.client_account_country) draftErrors.client_account_country = 'Bitte ausfüllen.';
        if (!draft.client_account_email || !validateEmail(draft.client_account_email)) {
            draftErrors.client_account_email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
        }
        return draftErrors;
    };

    const inlineErrorsFor = (examType:string):any => {
        const inline = inlineByExamType[examType];
        return (inline && inline.submitAttempted) ? validateDraft(inline.draft) : {};
    };

    // An untouched default form means "let FPSB pick".
    const isDraftDirty = (draft:IClientAccount) => !!(
        draft.client_account_type
        || draft.client_account_billing_name
        || draft.client_account_address
        || draft.client_account_zip
        || draft.client_account_city
        || draft.client_account_country
        || draft.client_account_email
    );

    const draftLabel = (draft:IClientAccount) => {
        const typeLabel = draft.client_account_type === companyType?.id ? 'Geschäftlich' : 'Privat';
        const name = draft.client_account_billing_name || 'Neues Konto';
        return `${name} – ${typeLabel}`;
    };

    // Create the inline account immediately so only its id is ever held/submitted.
    const saveInlineDraft = (examType:string) => {
        const inline = inlineByExamType[examType];
        if (!inline || inline.saving) {
            return;
        }
        patchInline(examType, { submitAttempted: true });
        if (Object.keys(validateDraft(inline.draft)).length > 0) {
            return;
        }
        const draft = inline.draft;
        const knownAccountIds = new Set(clientAccounts.map(account => String(account.id)));
        patchInline(examType, { saving: true, saveError: '' });
        postClientAccount(prepareAccount(draft)).then((res:any) => {
            const accountId = res?.payload?.account_id;
            if (res.type === ApiResponseType.OK && accountId) {
                // Keep the created account locally (for the select + review preview)
                // and assign its real id. The form only ever submits ids.
                const created:any = { ...draft, id: accountId, name: draftLabel(draft) };
                setClientAccounts(prev => [...prev, created]);
                assignSavedAccount(examType, String(accountId));
            } else {
                recoverFailedSave(examType, draft, knownAccountIds, 'Das Rechnungskonto konnte nicht angelegt werden.');
            }
        }).catch(() => {
            recoverFailedSave(examType, draft, knownAccountIds, 'Der Server ist aktuell nicht erreichbar.');
        });
    };

    // Assign a saved inline account and close its form. The draft is written right
    // away, not after the debounce, so leaving the page now cannot restore the open
    // form and lead to a second "Übernehmen".
    const assignSavedAccount = (examType:string, accountId:string) => {
        setAssignments(prev => ({ ...prev, [examType]: accountId }));
        closeInlineForm(examType);
        const latest = latestDraftRef.current;
        const remainingInline = { ...latest.inlineByExamType };
        delete remainingInline[examType];
        writeDraft(draftKey, {
            ...latest,
            assignments: { ...latest.assignments, [examType]: accountId },
            inlineByExamType: remainingInline,
            dismissedInline: { ...latest.dismissedInline, [examType]: true },
        }, profile?.real_number ?? null);
    };

    const sameValue = (a:any, b:any) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

    // A failed save may still have created the account (timeout, broken response).
    // Reload the list: exactly one new account matching the draft is taken as this
    // save; otherwise the user is asked to check the list before trying again.
    const recoverFailedSave = (examType:string, draft:IClientAccount, knownAccountIds:Set<string>, failure:string) => {
        const showError = () => patchInline(examType, {
            saving: false,
            saveError: `${failure} Bitte prüfen Sie, ob das Rechnungskonto bereits in der Auswahl erscheint, bevor Sie es erneut anlegen.`,
        });
        loadClientAccounts().then(accounts => {
            setClientAccounts(accounts);
            const matches = accounts.filter(account => !knownAccountIds.has(String(account.id))
                && sameValue(account.client_account_billing_name, draft.client_account_billing_name)
                && sameValue(account.client_account_zip, draft.client_account_zip)
                && sameValue(account.client_account_email, draft.client_account_email));
            if (matches.length === 1) {
                assignSavedAccount(examType, String(matches[0].id));
            } else {
                showError();
            }
        }).catch(showError);
    };

    // Country comes from a Select as an IOption; the API expects its value string.
    const prepareAccount = (data:IClientAccount):IClientAccount => {
        const clone:any = structuredClone(data);
        if (clone.client_account_country && typeof clone.client_account_country === 'object') {
            clone.client_account_country = clone.client_account_country.value;
        }
        if (transferTypeId != null) {
            clone.client_account_payment_type = transferTypeId;
        }
        clone.assigned_certificates = [];
        // The order confirmation covers these accounts, so no account email.
        clone.skip_notification = 1;
        return clone;
    };

    // Booked on its own, the DIN exam has no other order to share a billing
    // account with, so it always gets its own row.
    const dinOnly = formData.exam_type_din_competence && !formData.exam_type_efa_cfp && !formData.exam_type_cga_cfep;
    const billingRows = EXAM_TYPES
        .filter(option => formData[option.value])
        .filter(option => option.value !== 'exam_type_din_competence' || dinOnly || dinOtherBilling)
        .map(option => option.value);
    const accountsReady = !accountsLoading && !!privateType && !!companyType;

    // A new billing account is this screen's default. No setTouched — an untouched
    // default must not arm the navigation guard.
    useEffect(() => {
        if (!accountsReady) {
            return;
        }
        setInlineByExamType(prev => {
            let next = prev;
            billingRows.forEach(examType => {
                if (prev[examType] || assignments[examType] || dismissedInline[examType]) {
                    return;
                }
                if (next === prev) {
                    next = { ...prev };
                }
                next[examType] = makeInlineState();
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountsReady, billingRows.join(',')]);

    // Bookable exam-type combinations: EFA/CFP, the DIN competence exam (alone or
    // together with EFA/CFP), or the CGA/CFEP exam on its own — CGA/CFEP cannot be
    // combined with any other exam type. Returns '' when the selection is valid
    // (or empty). This conflict is shown live, so the "nothing selected" case is handled
    // separately in validate() (only surfaced on submit).
    const examTypeConflict = (data:any):string => {
        const efaCfp = data.exam_type_efa_cfp;
        const dinCompetence = data.exam_type_din_competence;
        const cgaCfep = data.exam_type_cga_cfep;
        if (cgaCfep && (efaCfp || dinCompetence)) {
            return 'Eine gleichzeitige Anmeldung zur Zentralprüfung Financial Planning/DIN-Kompetenzprüfung und zur Zentralprüfung Estate Planning ist nicht möglich, da die Prüfungen zeitgleich stattfinden.';
        }
        return '';
    };

    // "kein Abschluss" only exists for the DIN exam.
    const requiresDegree = (data:any) => !!(data.exam_type_efa_cfp || data.exam_type_cga_cfep);

    // The DIN exam on its own needs no proof of a Level I degree.
    const requiresCertificateProof = (data:any) => !(data.exam_type_din_competence && !requiresDegree(data));

    const isNoDegree = (educationProgramId:any) => educationProgramId !== ''
        && educationPrograms.some(entry => entry.no_degree && String(entry.id) === String(educationProgramId));

    const validate = (data:any) => {
        const validationErrors:any = {};
        const examConflict = examTypeConflict(data);
        if (examConflict) {
            validationErrors.exam_type = examConflict;
        } else if (!data.exam_type_efa_cfp && !data.exam_type_din_competence && !data.exam_type_cga_cfep) {
            validationErrors.exam_type = 'Bitte wählen Sie eine Prüfungsart aus.';
        } else if (EXAM_TYPES.some(option => data[option.value] && !allowedExamTypes.includes(option.value))) {
            // Same wording as the backend's limit check.
            validationErrors.exam_type = allowedExamTypes.length === 0
                ? 'Sie sind für diesen Prüfungstermin bereits angemeldet.'
                : efaRejoinsRegistration
                    ? 'Für diesen Prüfungstermin können Sie nur noch die EFA® / CFP®-Zentralprüfung hinzubuchen.'
                    : 'Für diesen Prüfungstermin können Sie nur noch die DIN-Kompetenzprüfung hinzubuchen.';
        }
        if (!data.participation) {
            validationErrors.participation = 'Bitte wählen Sie eine Option aus.';
        }
        if (!data.education_program) {
            validationErrors.education_program = 'Bitte wählen Sie eine Option aus.';
        }
        if (requiresCertificateProof(data)) {
            if (!data.certificate_proof) {
                validationErrors.certificate_proof = 'Bitte wählen Sie eine Option aus.';
            } else if (data.certificate_proof === 'certificate_attached' && files.length === 0) {
                validationErrors.documents = 'Bitte laden Sie das Abschlusszeugnis hoch.';
            }
        }
        if (!data.accept_agb) {
            validationErrors.accept_agb = 'Bitte bestätigen Sie diese Erklärung.';
        }
        if (!data.accept_privacy) {
            validationErrors.accept_privacy = 'Bitte bestätigen Sie diese Erklärung.';
        }
        if (!data.accept_withdrawal) {
            validationErrors.accept_withdrawal = 'Bitte bestätigen Sie diese Erklärung.';
        }
        if (!data.accept_exam_regulations) {
            validationErrors.accept_exam_regulations = 'Bitte bestätigen Sie diese Erklärung.';
        }
        // Only assigned ids are submitted — an unapplied form would be dropped.
        billingRows.forEach(examType => {
            const inline = inlineByExamType[examType];
            if (inline && !assignments[examType] && isDraftDirty(inline.draft)) {
                validationErrors['client_account_' + examType] = 'Bitte übernehmen Sie das neue Rechnungskonto oder verwerfen Sie es.';
            }
        });
        return validationErrors;
    };

    // Errors appear only after the first submit attempt, then update live as the user fixes them.
    const errors:any = submitAttempted ? validate(formData) : {};
    // Exception: an invalid exam-type combination is shown immediately, before any submit attempt.
    const liveExamTypeConflict = examTypeConflict(formData);
    if (liveExamTypeConflict) {
        errors.exam_type = liveExamTypeConflict;
    }
    // Bookable on its own, but the combination is the intended one.
    const showDinWithoutEfaWarning = formData.exam_type_din_competence && !formData.exam_type_efa_cfp && !dinJoinsRegistration;
    // DIN is priced as booked together with EFA/CFP, in the same or an earlier registration.
    const dinWithEfa = !!formData.exam_type_efa_cfp || dinJoinsRegistration;
    const degreeRequired = requiresDegree(formData);
    const certificateProofRequired = requiresCertificateProof(formData);
    const educationOptions:Array<{ value:string, label:string }> = educationPrograms
        .filter(entry => !(degreeRequired && entry.no_degree))
        .map(entry => ({ value: String(entry.id), label: entry.label }));

    // "EUR 600,00" style used in the fee copy.
    const eur = (value:number) => 'EUR ' + new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    // Zentralprüfung fee for the chosen participation: every repeat (1st and 2nd)
    // pays the repeat price, as in the backend (OrderService::isRepeatParticipation).
    const zentralPrice = (examType:string):{ net:number, gross:number }|null => {
        const price = prices[examType];
        if (!price) {
            return null;
        }
        const isRepeat = !!formData.participation && formData.participation !== 'participation_first_attempt';
        return (isRepeat && price.repeat) ? price.repeat : price;
    };
    // Show the fee area only for a valid, non-empty exam-type selection.
    const anyExamTypeSelected = EXAM_TYPES.some(option => formData[option.value]);
    const showPrices = anyExamTypeSelected && !liveExamTypeConflict;

    // Unchecked checkboxes are sent as an empty value, not "0".
    const checkboxValue = (checked:boolean) => (checked ? 1 : '');

    const buildPayload = (resolvedIds:{ [examType:string]:string }) => {
        const payload:any = {};
        EXAM_TYPES.forEach(option => { payload[option.value] = checkboxValue(formData[option.value]); });
        payload.participation = formData.participation || '';
        payload.education_program = formData.education_program || '';
        // A hidden proof section submits neither a proof option nor its uploads.
        payload.certificate_proof = certificateProofRequired ? (formData.certificate_proof || '') : '';
        payload.accept_exam_regulations = checkboxValue(formData.accept_exam_regulations);
        payload.accept_agb = checkboxValue(formData.accept_agb);
        payload.accept_privacy = checkboxValue(formData.accept_privacy);
        payload.accept_withdrawal = checkboxValue(formData.accept_withdrawal);
        payload.request_early_check = checkboxValue(formData.request_early_check);
        const submittedFiles = (certificateProofRequired && formData.certificate_proof === 'certificate_attached') ? files : [];
        payload.filenames = submittedFiles.map(file => file.filename);
        payload.payloads = submittedFiles.map(file => file.payload);
        // One billing account id per exam type; empty -> backend selects automatically.
        payload.client_account_id_efa_cfp = resolvedIds.exam_type_efa_cfp ?? '';
        payload.client_account_id_din_competence = resolvedIds.exam_type_din_competence ?? '';
        payload.client_account_id_cga_cfep = resolvedIds.exam_type_cga_cfep ?? '';
        return payload;
    };

    const handleSubmit = async (event:any) => {
        event.preventDefault();

        setSubmitAttempted(true);
        if (Object.keys(validate(formData)).length > 0) {
            return;
        }

        setIsLoading(true);
        setResponseMessage('');
        setResponseMessageClass('');

        // Inline accounts are created immediately (on "Übernehmen"), so assignments
        // already hold real account ids — only ids are submitted here.
        const resolvedIds:{ [examType:string]:string } = {};
        EXAM_TYPES.forEach(option => {
            // billingRows, not the exam type — a DIN account picked while DIN was
            // the only exam must not survive adding the EFA/CFP exam.
            resolvedIds[option.value] = (billingRows.includes(option.value) && assignments[option.value])
                ? String(assignments[option.value])
                : '';
        });

        postVerification(buildPayload(resolvedIds)).then(res => {
            setIsLoading(false);
            if (res.type === ApiResponseType.OK) {
                setTouched(false);
                clearDraft(draftKey);
                setCreatedOrderIds(Array.isArray((res.payload as any)?.order_ids) ? (res.payload as any).order_ids : []);
                setEarlyCheckRequested(!!formData.request_early_check);
                setSubmitted(true);
            } else {
                // A limit hit (errors.exam_type) means another registration got in
                // meanwhile; a project / deadline error means the exam closed while
                // the form was open. Refresh what is still bookable either way.
                const submitErrors = (res.payload as any)?.errors;
                if (submitErrors?.exam_type || submitErrors?.project || submitErrors?.registration_deadline) {
                    loadExamInfo();
                }
                showSubmitError(extractSubmitError(res.payload));
            }
        }).catch(() => {
            setIsLoading(false);
            showSubmitError('Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.');
        });
    };

    const showSubmitError = (message:string) => {
        setResponseMessage(message);
        setResponseMessageClass('error');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Single native submit handler: "Weiter" validates and advances to the review;
    // the review's "Jetzt kostenpflichtig anmelden" runs the real submit.
    const onFormSubmit = (event:any) => {
        event.preventDefault();
        if (!showReview) {
            setSubmitAttempted(true);
            if (Object.keys(validate(formData)).length > 0) {
                // Errors render on the next paint; bring the first one into view.
                setTimeout(() => {
                    const firstError = document.querySelector('#erstpruefung .form-error');
                    if (firstError) {
                        window.scrollTo({ top: firstError.getBoundingClientRect().top + window.pageYOffset - 120, behavior: 'smooth' });
                    }
                }, 0);
                return;
            }
            setConsentAt(new Date());
            setShowReview(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        handleSubmit(event);
    };

    // "Bearbeiten": leave the review and scroll back to the relevant form section.
    const editSection = (id:string) => {
        setShowReview(false);
        setTimeout(() => {
            const el = document.getElementById(id);
            if (el) {
                window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 20, behavior: 'smooth' });
            }
        }, 0);
    };

    // Resolve a per-exam-type billing assignment to an account object for display.
    // null assignment => the FPSB picks automatically.
    const resolveAccount = (assignment:string|null):IClientAccount|null => {
        if (!assignment) {
            return null;
        }
        return clientAccounts.find(a => String(a.id) === assignment) ?? null;
    };

    // Saved accounts hold the ISO code, inline-created ones the whole option.
    const accountCountryLabel = (country:any) => {
        if (country && typeof country === 'object') {
            return country.label ?? countryOption(country.value)?.label ?? country.value;
        }
        return countryOption(country)?.label ?? country;
    };

    // Readable billing-address block for the review, mirroring SingleAccountPreview.
    const accountPreview = (assignment:string|null) => {
        const account = resolveAccount(assignment);
        if (!account) {
            return <p className="m-0">Automatische Auswahl durch den FPSB Deutschland</p>;
        }
        const accountType = String(account.client_account_type ?? '');
        const typeLabel = companyType && accountType === String(companyType.id)
            ? 'Geschäftlich'
            : (privateType && accountType === String(privateType.id) ? 'Privat' : '');
        return (
            <div className="account-preview">
                { (account.client_account_billing_name || typeLabel) && <p className="m-0"><strong>{ account.client_account_billing_name }{ typeLabel ? ` | ${typeLabel}` : '' }</strong></p> }
            </div>
        );
    };

    // Review-derived values.
    const selectedZentralpruefungen = EXAM_TYPES.filter(o => o.value !== 'exam_type_din_competence' && formData[o.value]);
    const zentralGross = selectedZentralpruefungen.reduce((sum, o) => sum + (zentralPrice(o.value)?.gross ?? 0), 0);
    const dinSelected = formData.exam_type_din_competence;
    // The DIN price is the cost of one Gebühreneinheit: booked with the EFA/CFP
    // exam it takes one, on its own six — or nine without an accredited degree.
    const noDegreeSelected = isNoDegree(formData.education_program);
    const dinUnits = dinWithEfa
        ? 1
        : (noDegreeSelected ? 9 : 6);
    const dinGross = (prices.exam_type_din_competence?.gross ?? 0) * dinUnits;
    const totalGross = zentralGross + (dinSelected ? dinGross : 0);
    const mainExamType = (selectedZentralpruefungen[0]?.value) ?? 'exam_type_din_competence';
    const selectedEducationLabel = noDegreeSelected
        ? 'kein Abschluss'
        : (educationPrograms.find(entry => String(entry.id) === String(formData.education_program))?.label ?? '');
    const certificateProofLabel = CERTIFICATE_PROOF_OPTIONS.find(o => o.value === formData.certificate_proof)?.label ?? '';

    if (submitted) {
        return (
            <div id="erstpruefung" className="verification-confirmation">
                <h1 className="h3 mb-2">Vielen Dank für Ihre Anmeldung zur Zentralprüfung</h1>
                <p>Wir haben Ihre verbindliche Anmeldung zur Zentralprüfung des FPSB Deutschland erhalten. Wir haben Ihnen eine E-Mail mit Ihren Angaben zur Kontrolle geschickt. Bitte überprüfen Sie auch Ihren Spam-Ordner.</p>

                { earlyCheckRequested ? (
                    <p>Wir werden Ihre Anmeldung zeitnah prüfen und bearbeiten, da Sie explizit auf Ihr Widerspruchsrecht verzichtet haben.</p>
                ) : (
                    <>
                        <p>Wir werden Ihre Anmeldung nach Ablauf der 14-tägigen Widerspruchsfrist prüfen und bearbeiten. Sollten Sie eine sofortige Prüfung und Bearbeitung wünschen, klicken Sie bitte hier:</p>
                        <Button
                            buttonType="primary"
                            onClick={handleRequestEarlyCheck}
                            disabled={earlyCheckLoading || !createdOrderIds.length}
                        >Ich verlange ausdrücklich, dass der FPSB Deutschland mit der Ausführung der „Anmeldung zur Zentralprüfung“ vor Ablauf der Widerrufsfrist beginnt. Mir ist bekannt, dass mein Widerrufsrecht bei vollständiger Bearbeitung und Bestätigung der „Anmeldung zur Zentralprüfung“ durch den FPSB Deutschland erlischt.</Button>
                        { earlyCheckError && (
                            <div className="message error" role="alert"><span>{ earlyCheckError }</span></div>
                        ) }
                    </>
                ) }

                <p>Sie werden über die weiteren Schritte per E-Mail informiert.</p>
                <p>Die Gebührenrechnung für die Zentralprüfung versenden wir ausschließlich elektronisch an die im Rechnungskonto hinterlegte E-Mail-Adresse. Bitte kontrollieren Sie auch den Spam-Ordner.</p>
                <p>Sie können in Ihrem FPSB-Konto den Bearbeitungsstand einsehen und mögliche Optionen, wie beispielsweise einen Datei-Upload nachzureichender Abschlusszeugnisse, auswählen.</p>
                <p>Wir wünschen Ihnen schon heute eine gute Vorbereitung auf die Zentralprüfung und viel Erfolg!</p>
                <p className="m-0 text-xs">
                    <Link to="/auftraege">
                        <Button buttonType='primary'>Zurück zum Buchungsstatus der Zentralprüfung</Button>
                    </Link>
                </p>
            </div>
        );
    }

    // Wait for the bookable exam types, so no option shows that is then taken away.
    if (!examInfoLoaded || !certificatesLoaded) {
        return (
            <div id="erstpruefung">
                <h1 className="h3 mb-2">{ heading }</h1>
                <div className="loader"></div>
            </div>
        );
    }

    // No upcoming exam, deadline passed, or the one registration per exam project
    // already used: nothing to book, also when the route is opened directly.
    if (registrationStatus !== 'open') {
        const examDateLabel = (examInfo && examInfo.exam_date > 0) ? formatDateFromTimestamp(examInfo.exam_date, 'dd.mm.yyyy') : '';
        const deadlineLabel = (examInfo && examInfo.registration_deadline > 0) ? formatDateFromTimestamp(examInfo.registration_deadline, 'dd.mm.yyyy') : '';
        let notice:string;
        if (registrationStatus === 'no_exam') {
            notice = 'Derzeit ist kein Prüfungstermin zur Anmeldung verfügbar. Sobald ein neuer Termin feststeht, können Sie sich hier zur Zentralprüfung anmelden.';
        } else if (registrationStatus === 'deadline_passed') {
            notice = `Die Anmeldefrist für den Prüfungstermin${ examDateLabel ? ` am ${examDateLabel}` : '' } ist${ deadlineLabel ? ` am ${deadlineLabel}` : '' } abgelaufen. Eine Anmeldung ist für diesen Termin nicht mehr möglich.`;
        } else {
            notice = 'Sie sind für diesen Prüfungstermin bereits angemeldet. Den Stand Ihrer Anmeldung finden Sie im Buchungsstatus der Zentralprüfung.';
        }
        return (
            <div id="erstpruefung">
                <h1 className="h3 mb-2">{ heading }</h1>
                <div className="message" role="note"><span>{ notice }</span></div>
                <p className="m-0 text-xs">
                    <Link to="/auftraege">
                        <Button buttonType='primary'>Zum Buchungsstatus der Zentralprüfung</Button>
                    </Link>
                </p>
            </div>
        );
    }

    // The certificates rule out every type the project still offers (e.g. both
    // Zentralprüfungen already certified, or only DIN left for an EFA/CFP holder).
    if (bookableExamTypes.length === 0) {
        return (
            <div id="erstpruefung">
                <h1 className="h3 mb-2">{ heading }</h1>
                <div className="message" role="note"><span>Für Ihre bestehenden Zertifikate ist derzeit keine weitere Zentralprüfung buchbar.</span></div>
                <p className="m-0 text-xs">
                    <Link to="/auftraege">
                        <Button buttonType='primary'>Zum Buchungsstatus der Zentralprüfung</Button>
                    </Link>
                </p>
            </div>
        );
    }

    return (
        <div id="erstpruefung">
            <h1 className="h3 mb-2">{ heading }</h1>
            { !showReview && introContentTitle
                ? <DynamicContent title={introContentTitle} replacements={{
                    exam_date: (examInfo && examInfo.exam_date > 0) ? formatDateFromTimestamp(examInfo.exam_date, 'dd.mm.yyyy') : '',
                    registration_deadline: (examInfo && examInfo.registration_deadline > 0) ? formatDateFromTimestamp(examInfo.registration_deadline, 'dd.mm.yyyy') : '',
                }} />
                : (intro && <p>{ intro }</p>) }
            <Form
                id="initial-check-form"
                handleSubmit={onFormSubmit}
                isLoading={isLoading}
                // Entries survive as a draft, so no leave warning.
                unSavedChanges={false}
                responseMessage={responseMessage}
                responseMessageClass={responseMessageClass}
                customSubmit
            >
                { !showReview && (
                <>
                {/* Section 1 — Prüfungsart (with per-exam-type billing account) */}
                <section className="check-section" id="section-exam-types">
                    <h2 className="h5">Ich beantrage die Zulassung zur</h2>
                    { dinJoinsRegistration && (
                        <div className="message" role="note"><span>Sie sind für diesen Prüfungstermin bereits zur EFA® / CFP®-Zentralprüfung angemeldet{ linkedExamNumber ? ` (Prüfungsnummer ${linkedExamNumber})` : '' }. Sie können nur noch die DIN-Kompetenzprüfung hinzubuchen; sie wird Ihrer bestehenden Anmeldung zugeordnet.</span></div>
                    ) }
                    { efaRejoinsRegistration && (
                        <div className="message" role="note"><span>Sie sind für diesen Prüfungstermin bereits zur DIN-Kompetenzprüfung angemeldet{ linkedExamNumber ? ` (Prüfungsnummer ${linkedExamNumber})` : '' }. Sie können nur noch die EFA® / CFP®-Zentralprüfung hinzubuchen; sie wird Ihrer bestehenden Anmeldung zugeordnet.</span></div>
                    ) }
                    { offeredExamTypes.map(option => (
                        <div className="exam-type-row" key={option.value}>
                            <SingleCheckbox
                                name={option.value}
                                label={option.label}
                                checked={formData[option.value]}
                                onChange={() => toggleExamType(option.value)}
                            />
                        </div>
                    )) }
                    { errors.exam_type && (
                        <span className='form-error'>{ errors.exam_type }</span>
                    ) }
                    { showDinWithoutEfaWarning && (
                        <div className="message warning" role="status"><span>{ DIN_WITHOUT_EFA_WARNING }</span></div>
                    ) }
                </section>

                {/* Section 2 — Zentralprüfungsteilnahme */}
                <section className="check-section">
                    <h2 className="h5">Zentralprüfungsteilnahme</h2>
                    <Radios
                        name="participation"
                        options={PARTICIPATION_OPTIONS}
                        value={formData.participation}
                        onChange={(value:string) => updateField('participation', value)}
                    />
                    { errors.participation && (
                        <span className='form-error'>{ errors.participation }</span>
                    ) }
                </section>

                {/* Section 3 — Absolviertes akkreditiertes Aus- und Weiterbildungsprogramm */}
                <section className="check-section" id="section-training">
                    <h2 className="h5">Absolviertes akkreditiertes Aus- und Weiterbildungsprogramm, Level I</h2>
                    <Radios
                        name="education_program"
                        wrapperClassName="stacked"
                        options={educationOptions}
                        value={String(formData.education_program)}
                        onChange={(value:string) => updateField('education_program', value)}
                        error={errors.education_program}
                    />
                </section>

                {/* Section 4 — Nachweis des Abschlusszeugnisses Level I */}
                { certificateProofRequired && (
                <section className="check-section" id="section-certificate">
                    <h2 className="h5">Nachweis Abschluss Level I eines akkreditierten Aus- und Weiterbildungsprogramms</h2>
                    <div className="message" role="note"><span>{ CERTIFICATE_PROOF_HINT }</span></div>
                    <Radios
                        name="certificate_proof"
                        options={CERTIFICATE_PROOF_OPTIONS}
                        value={formData.certificate_proof}
                        onChange={(value:string) => updateField('certificate_proof', value)}
                        error={errors.certificate_proof}
                    />
                    { formData.certificate_proof === 'certificate_attached' && (
                        <div className="row">
                            <div className="col-6">
                                { draftHadUpload && files.length === 0 && (
                                    <p className="m-0">Bitte laden Sie Ihr Abschlusszeugnis erneut hoch – Dateien werden beim Zwischenspeichern Ihrer Eingaben nicht mitgespeichert.</p>
                                ) }
                                <FileDropzone
                                    name="documents"
                                    files={files}
                                    onChange={(updated) => { setTouched(true); setFiles(updated); }}
                                    maxFiles={5}
                                    maxSizeKb={2048}
                                    error={errors.documents}
                                />
                            </div>
                        </div>
                    ) }
                </section>
                ) }

                {/* Prüfungsgebühren — nur für die aktuell gewählten Prüfungsarten */}
                { showPrices && (
                    <section className="check-section prices">
                        <h2 className="h5">Prüfungsdauer & Prüfungsgebühren</h2>

                        { formData.exam_type_efa_cfp && (
                            <div className="price-block">
                                <p className="price-title">Zentralprüfung für Bewerber zur Zertifizierung als EFPA European Financial Advisor EFA® / CERTIFIED FINANCIAL PLANNER® – Zertifikatsträger</p>
                                <p>Prüfungsdauer: 160 Minuten zzgl. Lesezeit</p>
                                { zentralPrice('exam_type_efa_cfp') && (
                                    <p>Prüfungsgebühr: <strong>{eur(zentralPrice('exam_type_efa_cfp')!.net)}</strong> zzgl. MwSt. in gesetzlicher Höhe, entspricht <strong>{eur(zentralPrice('exam_type_efa_cfp')!.gross)}</strong> brutto</p>
                                ) }
                            </div>
                        ) }

                        { formData.exam_type_cga_cfep && (
                            <div className="price-block">
                                <p className="price-title">Zentralprüfung für Bewerber zur Zertifizierung als CGA® CERTIFIED GENERATIONS ADVISOR / CFEP® CERTIFIED FOUNDATION AND ESTATE PLANNER-Zertifikatsträger</p>
                                <p>Prüfungsdauer: 100 Minuten zzgl. Lesezeit</p>
                                { zentralPrice('exam_type_cga_cfep') && (
                                    <p>Prüfungsgebühr: <strong>{eur(zentralPrice('exam_type_cga_cfep')!.net)}</strong> zzgl. MwSt. in gesetzlicher Höhe, entspricht <strong>{eur(zentralPrice('exam_type_cga_cfep')!.gross)}</strong> brutto</p>
                                ) }
                            </div>
                        ) }

                        { formData.exam_type_din_competence && (
                            <div className="price-block">
                                <p className="price-title">DIN-Kompetenzprüfung für Bewerber zur Zertifizierung zum geprüften privaten Finanzplaner nach DIN ISO 22222:2006</p>
                                { !noDegreeSelected ? (
                                    <>
                                        <p><strong>mit Abschluss einer akkreditierten Aus- und Weiterbildung:</strong></p>
                                        <p>Prüfungsdauer: 160 Minuten zzgl. Lesezeit</p>
                                        { dinWithEfa ? (
                                            <p>Prüfungsgebühr: 1 Gebühreneinheit gem. DIN CERTCO (bei zeitgleicher Zulassung zur Zentralprüfung für Bewerber zur Zertifizierung als EFPA European Financial Advisor® EFA/CERTIFIED FINANCIAL PLANNER®-Zertifikatsträger)</p>
                                        ) : (
                                            <p>Prüfungsgebühr: 6 Gebühreneinheiten gem. DIN CERTCO (ohne zeitgleiche Zulassung zur Zentralprüfung für Bewerber zur Zertifizierung als EFPA European Financial Advisor® EFA/CERTIFIED FINANCIAL PLANNER®-Zertifikatsträger)</p>
                                        ) }
                                    </>
                                ) : (
                                    <>
                                        <p><strong>ohne Abschluss einer akkreditierten Aus- und Weiterbildung:</strong></p>
                                        <p>Prüfungsdauer: 240 Minuten zzgl. Lesezeit</p>
                                        <p>Prüfungsgebühr: 9 Gebühreneinheiten gem. DIN CERTCO</p>
                                    </>
                                ) }
                                { prices.exam_type_din_competence && (
                                    <p>Kosten einer Gebühreneinheit: <strong>{eur(prices.exam_type_din_competence.net)}</strong> zzgl. MwSt. in gesetzlicher Höhe, entspricht <strong>{eur(prices.exam_type_din_competence.gross)}</strong> brutto</p>
                                ) }
                            </div>
                        ) }
                    </section>
                ) }

                {/* Rechnungsstellung — Zahlungshinweis und Rechnungskonto je Prüfungsart */}
                { showPrices && (
                    <section className="check-section" id="section-billing">
                        <h2 className="h5">Rechnungsstellung</h2>
                        <p className="m-0">Sie erhalten nach Prüfung Ihrer Anmeldung eine Rechnung über die Prüfungsgebühr. Die Prüfungsgebühr ist innerhalb von 14 Tagen nach Rechnungsstellung ohne Abzüge zur Zahlung fällig. Bei Rechnungsstellung nach Anmeldeschluss ist die Rechnung spätestens fünf Bankarbeitstage vor dem Prüfungstermin zu zahlen.</p>
                        { EXAM_TYPES.filter(option => formData[option.value]).map(option => {
                            const isDin = option.value === 'exam_type_din_competence';
                            const showAccount = !isDin || dinOnly || dinOtherBilling;
                            return (
                                <div className="exam-type-row" key={option.value}>
                                    { isDin && !dinOnly && (
                                        <SingleCheckbox
                                            toggle
                                            name="din_other_billing"
                                            label="Für DIN-Kompetenzprüfung andere Rechnungsadresse verwenden"
                                            checked={dinOtherBilling}
                                            onChange={toggleDinOtherBilling}
                                        />
                                    ) }
                                    { showAccount && (
                                        <>
                                            <h3 className="h6 m-0">Rechnungskonto: { option.long }</h3>
                                            { !accountsReady ? (
                                                <div className="exam-type-account"><div className="loader inline"></div></div>
                                            ) : (
                                                <>
                                                    <ExamTypeAccountSelect
                                                        accounts={clientAccounts}
                                                        assignment={assignments[option.value]}
                                                        onAssign={(value) => handleAssign(option.value, value)}
                                                        onAddNew={() => openAddNew(option.value)}
                                                        inlineOpen={!!inlineByExamType[option.value]}
                                                        inlineDraft={inlineByExamType[option.value]?.draft ?? null}
                                                        inlineErrors={inlineErrorsFor(option.value)}
                                                        inlineSaving={!!inlineByExamType[option.value]?.saving}
                                                        inlineSaveError={inlineByExamType[option.value]?.saveError ?? ''}
                                                        onInlineChange={(next) => handleInlineChange(option.value, next)}
                                                        onInlineSave={() => saveInlineDraft(option.value)}
                                                        onInlineCancel={() => closeInlineForm(option.value)}
                                                        privateType={privateType}
                                                        companyType={companyType}
                                                    />
                                                    { errors['client_account_' + option.value] && (
                                                        <span className="form-error" role="alert">{ errors['client_account_' + option.value] }</span>
                                                    ) }
                                                </>
                                            ) }
                                        </>
                                    ) }
                                </div>
                            );
                        }) }
                    </section>
                ) }

                {/* Section 5 — Absichtserklärung */}
                <section className="check-section" id="section-consent">
                    <h2 className="h5">Absichtserklärung</h2>
                    <SingleCheckbox
                        name="accept_agb"
                        required
                        ariaLabel="Ich akzeptiere die Allgemeinen Geschäftsbedingungen"
                        label={<span>Ich akzeptiere die <a href="#" onClick={(e) => openLegalDocument(e, 'agb', 'AGB')}>Allgemeinen Geschäftsbedingungen</a> für die Buchung von Zentralprüfungen.</span>}
                        checked={formData.accept_agb}
                        onChange={() => updateField('accept_agb', !formData.accept_agb)}
                        error={errors.accept_agb}
                    />
                    <SingleCheckbox
                        name="accept_privacy"
                        required
                        ariaLabel="Ich habe die Hinweise zum Datenschutz zur Kenntnis genommen"
                        label={<span>Ich habe die <a href="#" onClick={(e) => openLegalDocument(e, 'privacy', 'Datenschutz')}>Hinweise zum Datenschutz</a> gelesen und erkenne sie an.</span>}
                        checked={formData.accept_privacy}
                        onChange={() => updateField('accept_privacy', !formData.accept_privacy)}
                        error={errors.accept_privacy}
                    />
                    <SingleCheckbox
                        name="accept_withdrawal"
                        required
                        ariaLabel="Ich habe die Widerrufsbelehrung zur Kenntnis genommen"
                        label={<span>Die <a href="#" onClick={(e) => openLegalDocument(e, 'withdrawal', 'Widerrufsbelehrung')}>Widerrufsbelehrung</a> habe ich zur Kenntnis genommen.</span>}
                        checked={formData.accept_withdrawal}
                        onChange={() => updateField('accept_withdrawal', !formData.accept_withdrawal)}
                        error={errors.accept_withdrawal}
                    />
                    <SingleCheckbox
                        name="accept_exam_regulations"
                        required
                        ariaLabel="Ich erkenne die Prüfungsordnung an"
                        label={<span>Mit meiner Anmeldung erkenne ich die <a href="#" onClick={(e) => openLegalDocument(e, 'exam_regulations', 'Prüfungsordnung')}>„Prüfungsordnung für Zentralprüfungen des Financial Planning Standards Board Deutschland e.V."</a> an.</span>}
                        checked={formData.accept_exam_regulations}
                        onChange={() => updateField('accept_exam_regulations', !formData.accept_exam_regulations)}
                        error={errors.accept_exam_regulations}
                    />
                    { /* Optional: express request for early performance (§ 356 Abs. 4 / § 357 Abs. 8 BGB). Wording to be confirmed by legal counsel. */ }
                    <SingleCheckbox
                        name="request_early_check"
                        ariaLabel="Ich verlange den vorzeitigen Beginn der Prüfung meiner Teilnahmevoraussetzungen"
                        label={<strong>Ich verlange ausdrücklich, dass der FPSB Deutschland mit der Ausführung der „Anmeldung zur Zentralprüfung“ vor Ablauf der Widerrufsfrist beginnt. Mir ist bekannt, dass mein Widerrufsrecht bei vollständiger Bearbeitung und Bestätigung der „Anmeldung zur Zentralprüfung“ durch den FPSB Deutschland erlischt.</strong>}
                        checked={formData.request_early_check}
                        onChange={() => updateField('request_early_check', !formData.request_early_check)}
                        error={errors.request_early_check}
                    />
                </section>
                </>
                ) }

                {/* Review / checkout — read-only summary shown after "Weiter" */}
                { showReview && (
                    <section className="check-section review">
                        <p>Bitte überprüfen Sie die Angaben Ihrer Anmeldung zur Zentralprüfung und schließen Sie die Anmeldung ab.</p>
                        <p><strong>Ihre Angaben:</strong></p>
                        <div className="review-table">
                            <div className="review-row">
                                <div className="review-label">Beantragte Zentralprüfung</div>
                                <div className="review-value">
                                    { dinJoinsRegistration ? (
                                        <>DIN-Kompetenzprüfung<p>als Ergänzung zu Ihrer bestehenden Anmeldung{ linkedExamNumber ? ` ${linkedExamNumber}` : '' }</p></>
                                    ) : (
                                        <>
                                            { selectedZentralpruefungen.map(o => o.long).join(', ') || '—' }
                                            { dinSelected && <p>zzgl. DIN-Kompetenzprüfung</p> }
                                            { efaRejoinsRegistration && <p>als Ergänzung zu Ihrer bestehenden Anmeldung{ linkedExamNumber ? ` ${linkedExamNumber}` : '' }</p> }
                                        </>
                                    ) }
                                </div>
                                <div className="review-edit"><button type="button" className="edit-link" onClick={() => editSection('section-exam-types')}>Bearbeiten</button></div>
                            </div>
                            <div className="review-row">
                                <div className="review-label">Datum und Uhrzeit der Zentralprüfung</div>
                                <div className="review-value">
                                    { examInfo && examInfo.exam_date ? formatDateFromTimestamp(examInfo.exam_date, 'dd.mm.yyyy') : 'wird noch mitgeteilt' }
                                    <p className="review-note">Der genaue Beginn wird Ihnen im Einladungsschreiben mitgeteilt, dessen Versand erfolgt nach offiziellem Anmeldeschluss.</p>
                                </div>
                                <div className="review-edit"></div>
                            </div>
                            <div className="review-row">
                                <div className="review-label">Ort der Zentralprüfung</div>
                                <div className="review-value">
                                    Frankfurt am Main
                                    <p className="review-note">Die genauen Adressdaten werden Ihnen im Einladungsschreiben mitgeteilt, dessen Versand erfolgt nach offiziellem Anmeldeschluss.</p>
                                </div>
                                <div className="review-edit"></div>
                            </div>
                            <div className="review-row">
                                <div className="review-label">Absolviertes Weiterbildungsprogramm</div>
                                <div className="review-value">{ selectedEducationLabel || '—' }</div>
                                <div className="review-edit"><button type="button" className="edit-link" onClick={() => editSection('section-training')}>Bearbeiten</button></div>
                            </div>
                            { certificateProofRequired && (
                            <div className="review-row">
                                <div className="review-label">Nachweis Abschlusszeugnis</div>
                                <div className="review-value">
                                    { formData.certificate_proof === 'certificate_attached'
                                        ? (files.length ? files.map(f => f.filename).join(', ') : certificateProofLabel)
                                        :  (certificateProofLabel || '—') }
                                </div>
                                <div className="review-edit"><button type="button" className="edit-link" onClick={() => editSection('section-certificate')}>Bearbeiten</button></div>
                            </div>
                            ) }
                            <div className="review-row">
                                <div className="review-label">Zustimmung zur Prüfungsordnung</div>
                                <div className="review-value">{ consentAt ? `Wurde erteilt am ${formatDate(consentAt, 'dd.mm.yyyy')} um ${formatDate(consentAt, 'hh:ii')} Uhr` : '—' }</div>
                                <div className="review-edit"><button type="button" className="edit-link" onClick={() => editSection('section-consent')}>Bearbeiten</button></div>
                            </div>
                            <div className="review-row">
                                <div className="review-label">Verzicht auf Widerrufsrecht</div>
                                <div className="review-value">{ formData.request_early_check
                                    ? `Vorzeitige Prüfung der Anmeldung ausdrücklich verlangt${consentAt ? ` am ${formatDate(consentAt, 'dd.mm.yyyy')} um ${formatDate(consentAt, 'hh:ii')} Uhr` : ''}`
                                    : 'Bearbeitung der Anmeldung nach Ablauf der Widerrufsfrist' }</div>
                                <div className="review-edit"><button type="button" className="edit-link" onClick={() => editSection('section-consent')}>Bearbeiten</button></div>
                            </div>
                            { dinSelected && dinOtherBilling && (
                                <div className="review-row">
                                    <div className="review-label">Angaben zur Rechnungsadresse für DIN-Kompetenzprüfung</div>
                                    <div className="review-value">{ accountPreview(assignments['exam_type_din_competence']) }</div>
                                    <div className="review-edit"><button type="button" className="edit-link" onClick={() => editSection('section-billing')}>Bearbeiten</button></div>
                                </div>
                            ) }
                            { dinSelected && (
                                <div className="review-row">
                                    <div className="review-label">Kosten DIN-Kompetenzprüfung</div>
                                    <div className="review-value">{ eur(dinGross) } inkl. MwSt. in gesetzlicher Höhe ({ dinUnits } { dinUnits === 1 ? 'Gebühreneinheit' : 'Gebühreneinheiten' } gem. DIN CERTCO)</div>
                                    <div className="review-edit"></div>
                                </div>
                            ) }
                            <div className="review-row">
                                <div className="review-label">Angaben zur Rechnungsadresse</div>
                                <div className="review-value">{ accountPreview(assignments[mainExamType]) }</div>
                                <div className="review-edit"><button type="button" className="edit-link" onClick={() => editSection('section-billing')}>Bearbeiten</button></div>
                            </div>
                            { selectedZentralpruefungen.length > 0 && (
                                <div className="review-row">
                                    <div className="review-label">Kosten der Zentralprüfung</div>
                                    <div className="review-value">{ eur(zentralGross) } inkl. MwSt. in gesetzlicher Höhe</div>
                                    <div className="review-edit"></div>
                                </div>
                            ) }
                            <div className="review-row review-total">
                                <div className="review-label">Gesamtsumme</div>
                                <div className="review-value">{ eur(totalGross) } inkl. MwSt. in gesetzlicher Höhe</div>
                                <div className="review-edit"></div>
                            </div>
                        </div>
                    </section>
                ) }

                {/* Weiter (form) / final submit (review) */}
                <Button
                    buttonType="primary"
                    type="submit"
                    icon="arrow"
                    disabled={isLoading}
                >{ showReview ? 'Kostenpflichtig die Anmeldung zur Zentralprüfung buchen' : 'Weiter' }</Button>
            </Form>
        </div>
    )
}
